<template>
  <div
    class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]"
  >
    <div class="w-full">
      <div v-if="isLoading" class="text-gray-600 font-sans py-4">
        <p>Loading data...</p>
      </div>
      <div
        v-else-if="!authStore.user?.organization_id"
        class="p-4 bg-yellow-50 font-sans border border-yellow-200 rounded-md mb-4"
      >
        <p class="text-amber-700">Unable to load organization data. Please try refreshing the page.</p>
        <button
          @click="authStore.fetchUserData"
          class="bg-amber-600 font-sans text-white px-4 py-1 rounded-md mt-2"
        >
          Retry Loading Organization Data
        </button>
      </div>
      <div v-else>
        <QuestionField
          v-model="societalChangeState.userAnswer"
          :label="societalChangeLabel"
          :question="societalChangeText"
          placeholder="Enter the challenges or needs your organization addresses..."
        />
        <p v-if="societalChangeError" class="text-red-500 font-sans">
          {{ societalChangeError }}
        </p>
        <p
          v-if="v$.societalChangeState.userAnswer.$error"
          class="text-red-500 font-sans"
        >
          {{ v$.societalChangeState.userAnswer.$errors[0].$message }}
        </p>
        <div class="flex items-center mt-2 mb-4">
          <span v-if="isSavingSocietal" class="font-minion text-gray-600"
            >Saving Societal Challenge...</span
          >
          <span v-if="saveSuccessfulSocietal" class="font-minion text-green-600"
            >Societal Challenge saved successfully</span
          >
        </div>

        <QuestionField
          v-model="solutionState.userAnswer"
          :label="solutionLabel"
          :question="solutionText"
          placeholder="Enter how your organization is addressing these challenges..."
        />
        <p v-if="solutionError" class="text-red-500 font-sans">
          {{ solutionError }}
        </p>
        <p
          v-if="v$.solutionState.userAnswer.$error"
          class="text-red-500 font-sans"
        >
          {{ v$.solutionState.userAnswer.$errors[0].$message }}
        </p>
        <div class="flex items-center mt-2">
          <span v-if="isSavingSolution" class="font-minion text-gray-600"
            >Saving solution...</span
          >
          <span v-if="saveSuccessfulSolution" class="font-minion text-green-600"
            >Solution saved successfully</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from "vue";
import QuestionField from "@/components/QuestionField.vue";
import { useAuthStore } from "@/stores/auth";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import global from "@/global";
import { useAutoSave } from "@/composables/useAutoSave";
import { useStepValidation } from "@/composables/useStepValidation";

const isLoading = ref(true);

// Add separate auto-save related refs for each field
const isSavingSocietal = ref(false);
const isSavingSolution = ref(false);
const saveSuccessfulSocietal = ref(false);
const saveSuccessfulSolution = ref(false);
const lastSavedSocietalChange = ref("");
const lastSavedSolution = ref("");

// Societal Change question refs
const societalChangeLabel = ref("");
const societalChangeText = ref("");
const societalChangeError = ref("");
const societalChangeState = reactive({ userAnswer: "" });
const societalChangeCategoryId = ref("");
const societalChangeQuestionId = ref("");

// Solution question refs
const solutionLabel = ref("");
const solutionText = ref("");
const solutionError = ref("");
const solutionState = reactive({ userAnswer: "" });
const solutionCategoryId = ref("");
const solutionQuestionId = ref("");

// Validation rules
const rules = {
  societalChangeState: {
    userAnswer: {
      required: helpers.withMessage(
        "Societal Change answer is required!",
        required
      ),
    },
  },
  solutionState: {
    userAnswer: {
      required: helpers.withMessage("Solution answer is required!", required),
    },
  },
};
const v$ = useVuelidate(rules, { societalChangeState, solutionState }, {
  $lazy: true // This makes validation lazy - only validates on submit
});

// Fetching the auth store
const authStore = useAuthStore();

// Hide success message after delay
const hideSuccessMessage = (field) => {
  setTimeout(() => {
    if (field === "societal") {
      saveSuccessfulSocietal.value = false;
    } else {
      saveSuccessfulSolution.value = false;
    }
  }, 2000);
};

// Add back the save functions
async function saveSocietalChange() {
  // Don't save if answer hasn't changed
  if (societalChangeState.userAnswer === lastSavedSocietalChange.value) {
    return;
  }

  // Get organization ID from auth store
  const organizationId = authStore.user.organization_id;

  if (!organizationId) {
    societalChangeError.value = "Invalid organization ID. Please try refreshing the page.";
    return;
  }

  isSavingSocietal.value = true;
  saveSuccessfulSocietal.value = false;

  try {
    const societalChangeRes = await fetch(
      `${global.API_URL}/answers`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          category_id: societalChangeCategoryId.value,
          question_id: societalChangeQuestionId.value,
          answer: societalChangeState.userAnswer,
        }),
      }
    );

    if (!societalChangeRes.ok) {
      throw new Error(`HTTP error! Status: ${societalChangeRes.status}`);
    }

    const data = await societalChangeRes.json();
    if (data.status) {
      societalChangeError.value = "";
      lastSavedSocietalChange.value = societalChangeState.userAnswer;
      saveSuccessfulSocietal.value = true;
      hideSuccessMessage("societal");
    } else {
      throw new Error("Failed to save Societal Challenge answer");
    }
  } catch (error) {
    societalChangeError.value = "Failed to save your answer. Please try again.";
    console.error("Failed to save Societal Challenge answer:", error);
  } finally {
    isSavingSocietal.value = false;
  }
}

async function saveSolutionAnswer() {
  // Don't save if answer hasn't changed
  if (solutionState.userAnswer === lastSavedSolution.value) {
    return;
  }

  // Get organization ID from auth store
  const organizationId = authStore.user.organization_id;

  if (!organizationId) {
    solutionError.value = "Invalid organization ID. Please try refreshing the page.";
    return;
  }

  isSavingSolution.value = true;
  saveSuccessfulSolution.value = false;

  try {
    const solutionRes = await fetch(
      `${global.API_URL}/answers`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          category_id: solutionCategoryId.value,
          question_id: solutionQuestionId.value,
          answer: solutionState.userAnswer,
        }),
      }
    );

    if (!solutionRes.ok) {
      throw new Error(`HTTP error! Status: ${solutionRes.status}`);
    }

    const data = await solutionRes.json();
    if (data.status) {
      solutionError.value = "";
      lastSavedSolution.value = solutionState.userAnswer;
      saveSuccessfulSolution.value = true;
      hideSuccessMessage("solution");
    } else {
      throw new Error("Failed to save solution answer");
    }
  } catch (error) {
    solutionError.value = "Failed to save your answer. Please try again.";
    console.error("Failed to save solution answer:", error);
  } finally {
    isSavingSolution.value = false;
  }
}

// Using the auto-save composable for both fields separately
const { setupAutoSave: setupSocietalChangeAutoSave } = useAutoSave(
  () => societalChangeState.userAnswer,
  saveSocietalChange,
  () => true, // Remove validation check from auto-save
  1000
);

const { setupAutoSave: setupSolutionAutoSave } = useAutoSave(
  () => solutionState.userAnswer,
  saveSolutionAnswer,
  () => true, // Remove validation check from auto-save
  1000
);

// getUserAnswers into two separate functions
async function getSocietalChangeAnswer() {
  // Get organization ID from auth store
  const organizationId = authStore.user.organization_id;

  if (!organizationId) {
    console.error("Invalid organizationId:", organizationId);
    societalChangeError.value = "Invalid organization ID. Please try refreshing the page.";
    return;
  }

  if (societalChangeQuestionId.value) {
    try {
      const societalChangeRes = await fetch(
        `${global.API_URL}/answers?question_id=${societalChangeQuestionId.value}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
          },
        }
      );

      if (!societalChangeRes.ok) {
        throw new Error(`HTTP error! Status: ${societalChangeRes.status}`);
      }

      const societalChangeData = await societalChangeRes.json();
      let answer = societalChangeData.answer;
      if (answer != null) {
        societalChangeState.userAnswer = answer.answer;
        lastSavedSocietalChange.value = answer.answer;
      }
    } catch (error) {
      console.error("Failed to fetch Societal Challenge answer:", error);
      societalChangeError.value =
        "Failed to load your previous answer. You can still create a new one.";
    }
  }
}

async function getSolutionAnswer() {
  // Get organization ID from auth store
  const organizationId = authStore.user.organization_id;

  if (!organizationId) {
    console.error("Invalid organizationId:", organizationId);
    solutionError.value = "Invalid organization ID. Please try refreshing the page.";
    return;
  }

  if (solutionQuestionId.value) {
    try {
      const solutionRes = await fetch(
        `${global.API_URL}/answers?question_id=${solutionQuestionId.value}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
          },
        }
      );

      if (!solutionRes.ok) {
        throw new Error(`HTTP error! Status: ${solutionRes.status}`);
      }

      const solutionData = await solutionRes.json();
      let answer = solutionData.answer;
      if (answer != null) {
        solutionState.userAnswer = answer.answer;
        lastSavedSolution.value = answer.answer;
      }
    } catch (error) {
      console.error("Failed to fetch solution answer:", error);
      solutionError.value =
        "Failed to load your previous answer. You can still create a new one.";
    }
  }
}

// Update the getUserAnswers function to use the new split functions
async function getUserAnswers() {
  await getSocietalChangeAnswer();
  await getSolutionAnswer();
}

// Custom validation function for the useStepValidation composable
const validateStep = async () => {
  // Trigger Vuelidate validation for both fields
  const result = await v$.value.$validate();
  
  // If there are validation errors in either field, immediately return false
  if (!result) {
    return false;
  }

  // Check for unsaved changes in either field
  const hasSocietalChanges =
    societalChangeState.userAnswer !== lastSavedSocietalChange.value;
  const hasSolutionChanges =
    solutionState.userAnswer !== lastSavedSolution.value;

  try {
    // Save any unsaved changes
    if (hasSocietalChanges) {
      await saveSocietalChange();
    }
    if (hasSolutionChanges) {
      await saveSolutionAnswer();
    }
    return true;
  } catch (error) {
    console.error("Failed to save answers:", error);
    societalChangeError.value = hasSocietalChanges
      ? "Please ensure your Societal Challenge answer is saved before proceeding."
      : "";
    solutionError.value = hasSolutionChanges
      ? "Please ensure your solution answer is saved before proceeding."
      : "";
    return false;
  }
};

// Fetching question data directly using fetch API
async function fetchQuestionData() {
  try {
    // Fetch Societal Challenge question data
    const societalChangeRes = await fetch(
      `${global.API_URL}/questions/societal_change_change`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      }
    );

    if (!societalChangeRes.ok) {
      throw new Error(`HTTP error! Status: ${societalChangeRes.status}`);
    }

    const societalChangeData = await societalChangeRes.json();
    societalChangeLabel.value = societalChangeData.question.label;
    societalChangeText.value = societalChangeData.question.title;
    societalChangeCategoryId.value = societalChangeData.question.category_id;
    societalChangeQuestionId.value = societalChangeData.question.id;

    // Fetch solution question data
    const solutionRes = await fetch(
      `${global.API_URL}/questions/solution_solution`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      }
    );

    if (!solutionRes.ok) {
      throw new Error(`HTTP error! Status: ${solutionRes.status}`);
    }

    const solutionData = await solutionRes.json();
    solutionLabel.value = solutionData.question.label;
    solutionText.value = solutionData.question.title;
    solutionCategoryId.value = solutionData.question.category_id;
    solutionQuestionId.value = solutionData.question.id;
  } catch (error) {
    societalChangeError.value =
      "Failed to load question data. Please try again.";
    solutionError.value = "Failed to load question data. Please try again.";
    console.error(error);
  }
}

// Use the step validation composable
useStepValidation({
  initialValidState: false, // Start with invalid state
  validateFn: validateStep,
});

onMounted(async () => {
  try {
    isLoading.value = true;
    societalChangeError.value = "";
    solutionError.value = "";

    // First ensure we have the latest user data with organization ID
    await authStore.fetchUserData();
    console.log("Organization ID after fetch:", authStore.user?.organization_id);

    if (!authStore.user?.organization_id) {
      societalChangeError.value = "Unable to load organization data. Please try refreshing the page.";
      solutionError.value = "Unable to load organization data. Please try refreshing the page.";
      console.error("No organization ID available");
      return;
    }

    // Then load the question data
    await fetchQuestionData();
    console.log("Societal Change Question ID after fetch:", societalChangeQuestionId.value);
    console.log("Solution Question ID after fetch:", solutionQuestionId.value);

    if (!societalChangeQuestionId.value || !solutionQuestionId.value) {
      societalChangeError.value = "Unable to load question data. Please try refreshing the page.";
      solutionError.value = "Unable to load question data. Please try refreshing the page.";
      console.error("Question IDs not available");
      return;
    }

    // Set up auto-save watchers
    setupSocietalChangeAutoSave(() => societalChangeState.userAnswer);
    setupSolutionAutoSave(() => solutionState.userAnswer);

    // Finally fetch the user's answers
    await getUserAnswers();
    console.log("Societal Change Answer loaded:", societalChangeState.userAnswer);
    console.log("Solution Answer loaded:", solutionState.userAnswer);

  } catch (error) {
    console.error("Error during component initialization:", error);
    societalChangeError.value = "An error occurred while loading the data. Please try refreshing the page.";
    solutionError.value = "An error occurred while loading the data. Please try refreshing the page.";
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
 
</style>